import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Tablo,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  agacGetir,
  alanlariGetir,
  kaynakHavuzu,
  ogrenciKaynakCikar,
  ogrenciKaynakEkle,
  ogrenciKaynakIlerleme,
  ogrenciKaynakTopluEkle,
  ogrenciKaynaklari,
  type OgrenciKaynakSatiri,
  type TopluKaynakSonuc,
} from "./api";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * ÖĞRENCİNİN KAYNAK LİSTESİ — F-RES-02
 *
 * ================================================================
 * ÖLÇÜLEMEDİ ≠ SIFIR — BU EKRANIN OMURGASI
 * ================================================================
 * `ilerlemeYuzdesi` toplam sayfa bilinmiyorsa `null`.
 * `basariOrani` yeterli soru çözülmemişse `null` (F-RES-04).
 * İkisi de "—" gösteriliyor.
 *
 * "%0" yazmak iki ayrı yanlışa yol açardı: öğrenci hiç denenmediği bir
 * kaynakta başarısız görünür, ve uzman o kaynağı "zor" sanıp listeden
 * çıkarırdı. Sunucu bu ayrımı zaten yapıyor; ekranın işi onu
 * BOZMAMAK.
 *
 * ================================================================
 * LİSTEDEN ÇIKARMA SİLME DEĞİL
 * ================================================================
 * Geçmiş program görevleri o kayda bakıyor. Çıkarılan kaynak
 * arşivleniyor; görevler bozulmuyor.
 */
const HUKUM: Record<string, { etiket: string; ton: "notr" | "onay" | "uyari" }> = {
  OLCULMEDI: { etiket: "Ölçülmedi", ton: "notr" },
  IYI: { etiket: "İyi gidiyor", ton: "onay" },
  ZORLANIYOR: { etiket: "Zorlanıyor", ton: "uyari" },
};

/**
 * ================================================================
 * SUNUCU İKİ FARKLI ÖLÇEK DÖNÜYOR — ALAN ADLARI BUNU SÖYLÜYOR
 * ================================================================
 * `ilerlemeYuzdesi` zaten **0–100** (sunucu `Math.round(x*100)` ile
 * hesaplıyor). `basariOrani` ise **0–1** (`dogru / cevaplanan`).
 *
 * 02.09.2026'da canlı denemede ikisini tek fonksiyondan geçirdim ve
 * ekranda **%5000** çıktı. Adlandırma doğruydu — "yüzde" ile "oran"
 * farklı şeyler — ama tek bir `yuzde()` yardımcısı o farkı sildi.
 *
 * İki ayrı fonksiyon bilerek: ortak bir tanesi, çağıran tarafın
 * hangi ölçeği gönderdiğini HATIRLAMASINI gerektirir ve o hatırlama
 * bir gün unutulur. Adları hangisinin ne beklediğini söylüyor.
 */
function yuzdeGoster(yuzde100: number | null): string {
  // ÖLÇÜLEMEDİ ≠ %0 — dosya başındaki gerekçe.
  return yuzde100 === null ? "—" : `%${Math.round(yuzde100)}`;
}

function oranGoster(oran01: number | null): string {
  return oran01 === null ? "—" : `%${Math.round(oran01 * 100)}`;
}

export function OgrenciKaynaklari({
  ogrenciId,
  ogrenciAdi,
  duzenleyebilir = false,
  kaldirabilir = false,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  duzenleyebilir?: boolean;
  kaldirabilir?: boolean;
}) {
  const [acik, setAcik] = useState<"kapali" | "tek" | "toplu">("kapali");

  const liste = useQuery({
    queryKey: ["ogrenci-kaynak", ogrenciId],
    queryFn: () => ogrenciKaynaklari(ogrenciId),
    retry: false,
  });

  if (liste.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Kaynak listesi alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki
        aciklama={`${ogrenciAdi} — öğrenciye özel kaynak kitaplar ve ilerleme durumu.`}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-notr-600 text-sm">
          {liste.data.length === 0
            ? `${ogrenciAdi} için kaynak yok`
            : `${liste.data.length} kaynak`}
        </p>
        {duzenleyebilir && (
          <div className="flex flex-wrap gap-2">
            <Buton
              gorunum="ikincil"
              onClick={() => setAcik((a) => (a === "toplu" ? "kapali" : "toplu"))}
            >
              {acik === "toplu" ? "Kapat" : "Toplu ekle"}
            </Buton>
            <Buton onClick={() => setAcik((a) => (a === "tek" ? "kapali" : "tek"))}>
              {acik === "tek" ? "Kapat" : "Kaynak ekle"}
            </Buton>
          </div>
        )}
      </div>

      {acik === "tek" && <KaynakEkle ogrenciId={ogrenciId} onKapat={() => setAcik("kapali")} />}
      {acik === "toplu" && (
        <KaynakTopluEkle ogrenciId={ogrenciId} onKapat={() => setAcik("kapali")} />
      )}

      {liste.data.length === 0 ? (
        /*
          ================================================================
          BOŞ HÂL, YETKİSİ OLMAYANA YAPAMAYACAĞI ŞEYİ VAAT EDİYORDU
          ================================================================
          Açıklama tek bir metindi: "Havuzdan seçebilir ya da havuzda
          olmayan bir kitabı serbest adla EKLEYEBİLİRSİNİZ." Düğme ise
          `duzenleyebilir` false iken hiç çizilmiyordu.

          Yani yetkisi olmayan uzman "ekleyebilirsiniz" cümlesini
          okuyup ekleme yolunu arıyor ve bulamıyordu. Bu, çizilmeyen
          düğmenin sessizliğinden daha kötü: sessizlik bilgi vermiyor,
          bu cümle YANLIŞ bilgi veriyordu.

          İki ayrı metin, çünkü iki ayrı kullanıcı: biri ne yapacağını
          öğreniyor, öteki neden yapamadığını.

          ROL ADI YAZILMIYOR ve bu bilinçli. İlk yazdığım cümle
          "akademik koordinatör ekleyebilir" diyordu; ölçtüm ve
          YANLIŞTI — `akademik.kaynak.ogrenci_duzenle` izni seed'de
          `OPS_ADMIN` (ALL) ve `COACH` (ASSIGNED) rollerinde, akademik
          koordinatörde değil.

          Doğrusunu yazmak da yetmezdi: CLAUDE.md "roller değişkendir,
          izinler sabittir" diyor ve firma bu rolleri yeniden
          adlandırabilir. Ekrana gömülen bir rol adı, adlandırma
          değiştiği gün sessizce yanlış olur — bugün beş kez
          yakaladığımız desenin ta kendisi. O yüzden cümle KİME
          gidileceğini işlevle söylüyor, unvanla değil.
        */
        <BosDurum
          ikon="mufredat"
          baslik="Bu öğrencinin kaynak listesi boş"
          aciklama={
            duzenleyebilir
              ? "Program hazırlarken görevlere kaynak yazabilmek için önce listeye kitap eklemek gerekiyor. Havuzdan seçebilir ya da havuzda olmayan bir kitabı serbest adla ekleyebilirsiniz."
              : "Program hazırlarken görevlere kaynak yazabilmek için listede kitap olması gerekiyor. Bu listeyi düzenleme yetkisi sizde değil — öğrencinin kendi uzmanı ya da operasyon yetkisi olan bir yönetici ekleyebilir."
          }
          eylem={
            duzenleyebilir ? <Buton onClick={() => setAcik("tek")}>Kaynak ekle</Buton> : undefined
          }
        />
      ) : (
        <Kart gorunum="unk" olarak="div" className="p-2 md:p-4">
          <Tablo
            baslikTuru="degrade"
            satirlar={liste.data}
            anahtar={(k) => k.id}
            kartBasligi={0}
            sutunlar={[
              {
                baslik: "Kaynak",
                mobil: true,
                className: "font-medium",
                hucre: (k) => (
                  <span className="flex flex-wrap items-center gap-1.5">
                    {k.ad}
                    {/*
                    HAVUZDAN ÇIKARILMIŞ KAYNAK İŞARETLENİYOR. Kayıt
                    öğrencide duruyor ve ilerlemesi korunuyor, ama
                    uzman bunun artık havuzda olmadığını bilmeli —
                    yoksa "neden yeni öğrenciye ekleyemiyorum"
                    sorusunun cevabı hiçbir yerde olmaz.
                  */}
                    {k.havuzdanCikarildiMi && <Rozet ton="uyari">Havuzdan çıkarıldı</Rozet>}
                    {!k.havuzdanMi && <Rozet ton="notr">Serbest</Rozet>}
                  </span>
                ),
              },
              { baslik: "Ders", mobil: true, hucre: (k) => k.ders },
              {
                baslik: "İlerleme",
                mobil: true,
                hucre: (k) => (
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums">{yuzdeGoster(k.ilerlemeYuzdesi)}</span>
                    {k.kacinciSayfa !== null && k.toplamSayfa !== null && (
                      <span className="text-notr-600 text-xs">
                        {k.kacinciSayfa}/{k.toplamSayfa}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                baslik: "Başarı",
                sayisal: true,
                /* F-RES-04 — yeterli soru çözülmediyse `null`. */
                hucre: (k) => oranGoster(k.basariOrani),
              },
              { baslik: "Çözülen", sayisal: true, hucre: (k) => k.cozulenSoru },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (k) => {
                  const h = HUKUM[k.zorlukHukmu] ?? { etiket: k.zorlukHukmu, ton: "notr" as const };
                  return <Rozet ton={h.ton}>{h.etiket}</Rozet>;
                },
              },
              ...(duzenleyebilir || kaldirabilir
                ? [
                    {
                      baslik: "İşlem",
                      mobil: true,
                      hucre: (k: OgrenciKaynakSatiri) => (
                        <SatirIslemleri
                          ogrenciId={ogrenciId}
                          kaynak={k}
                          duzenleyebilir={duzenleyebilir}
                          kaldirabilir={kaldirabilir}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Kart>
      )}
    </div>
  );
}

function SatirIslemleri({
  ogrenciId,
  kaynak,
  duzenleyebilir,
  kaldirabilir,
}: {
  ogrenciId: string;
  kaynak: OgrenciKaynakSatiri;
  duzenleyebilir: boolean;
  kaldirabilir: boolean;
}) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState<"kapali" | "ilerleme" | "cikar">("kapali");
  const [sayfa, setSayfa] = useState(String(kaynak.kacinciSayfa ?? ""));
  const [test, setTest] = useState(String(kaynak.kacinciTest ?? ""));
  const [puan, setPuan] = useState(String(kaynak.uzmanPuani ?? ""));

  const tazele = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["ogrenci-kaynak", ogrenciId] });
  };

  const sayi = (v: string): number | null => (v.trim() === "" ? null : Number(v));

  const kaydet = useMutation({
    mutationFn: () =>
      ogrenciKaynakIlerleme(ogrenciId, kaynak.id, {
        kacinciSayfa: sayi(sayfa),
        kacinciTest: sayi(test),
        uzmanPuani: sayi(puan),
      }),
    onSuccess: async () => {
      setAcik("kapali");
      await tazele();
    },
  });

  const cikar = useMutation({
    mutationFn: () => ogrenciKaynakCikar(ogrenciId, kaynak.id),
    onSuccess: async () => {
      setAcik("kapali");
      await tazele();
    },
  });

  if (acik === "ilerleme") {
    return (
      <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-2 border p-3">
        <div className="grid gap-2 sm:grid-cols-3">
          <Alan
            etiket="Kaçıncı sayfa"
            type="number"
            value={sayfa}
            onChange={(e) => setSayfa(e.target.value)}
            yardim={
              kaynak.toplamSayfa === null
                ? "Kitabın toplam sayfası bilinmiyor — yüzde hesaplanmaz."
                : `Toplam ${kaynak.toplamSayfa} sayfa.`
            }
          />
          <Alan
            etiket="Kaçıncı test"
            type="number"
            value={test}
            onChange={(e) => setTest(e.target.value)}
          />
          <Alan
            etiket="Uzman puanı"
            type="number"
            min={1}
            max={5}
            value={puan}
            onChange={(e) => setPuan(e.target.value)}
            yardim="1–5. Kaynağın öğrenciye uygunluğu."
          />
        </div>
        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={kaydet.isPending} onClick={() => kaydet.mutate()}>
            Kaydet
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setAcik("kapali")}>
            Vazgeç
          </Buton>
        </div>
      </div>
    );
  }

  if (acik === "cikar") {
    return (
      <div className="rounded-kart shadow-kart border-uyari-200 bg-uyari-50 flex flex-col gap-2 border p-3">
        {/*
          ÇIKARMA SİLME DEĞİL. Geçmiş program görevleri bu kayda
          bakıyor; silinseydi o görevlerin kaynağı boşa düşerdi.
        */}
        <p className="text-uyari-800 text-xs">
          <strong>{kaynak.ad}</strong> listeden çıkarılacak. <strong>Silinmiyor:</strong> bu
          kaynağın yazıldığı geçmiş program görevleri olduğu gibi kalıyor. Yalnızca yeni görevlerde
          seçilemez olur.
        </p>
        {cikar.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {cikar.error instanceof ApiHatasi ? cikar.error.message : "Çıkarılamadı."}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={cikar.isPending} onClick={() => cikar.mutate()}>
            Listeden çıkar
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setAcik("kapali")}>
            Vazgeç
          </Buton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {duzenleyebilir && (
        <Buton gorunum="sessiz" onClick={() => setAcik("ilerleme")}>
          İlerleme
        </Buton>
      )}
      {kaldirabilir && (
        <Buton gorunum="sessiz" onClick={() => setAcik("cikar")}>
          Çıkar
        </Buton>
      )}
    </div>
  );
}

/**
 * Listeye kaynak ekleme — havuzdan ya da SERBEST ADLA.
 *
 * Serbest ad bilinçli: uzman havuzun doldurulmasını beklemek zorunda
 * değil. Havuz yönetimi ayrı bir izin ve ayrı bir kişide olabilir;
 * beklemek, öğrencinin programının gecikmesi demek olurdu.
 */
function KaynakEkle({ ogrenciId, onKapat }: { ogrenciId: string; onKapat: () => void }) {
  const qc = useQueryClient();
  const [alanId, setAlanId] = useState("");
  const [dersId, setDersId] = useState("");
  const [kitapId, setKitapId] = useState("");
  const [ozelAd, setOzelAd] = useState("");

  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });
  const agac = useQuery({
    queryKey: ["agac", alanId],
    queryFn: () => agacGetir(alanId),
    enabled: alanId !== "",
    retry: false,
  });
  const havuz = useQuery({
    queryKey: ["kaynak-havuz", dersId, "", false],
    queryFn: () => kaynakHavuzu({ ders: dersId || undefined }),
    enabled: dersId !== "",
    retry: false,
  });

  const ekle = useMutation({
    mutationFn: () =>
      ogrenciKaynakEkle(ogrenciId, {
        dersId,
        kitapId: kitapId || null,
        ozelAd: kitapId ? null : ozelAd.trim() || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["ogrenci-kaynak", ogrenciId] });
      onKapat();
    },
  });

  const dersler = (agac.data ?? []).filter((d) => d.tur === "ders");
  const eksik = dersId === "" || (kitapId === "" && ozelAd.trim() === "");

  return (
    <Kart
      baslik="Listeye kaynak ekle"
      aciklama="Havuzdan seçin ya da havuzda olmayan bir kitabı serbest adla yazın — havuzun doldurulmasını beklemek zorunda değilsiniz."
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Secim
            etiket="Alan"
            value={alanId}
            onChange={(e) => {
              setAlanId(e.target.value);
              setDersId("");
              setKitapId("");
            }}
          >
            <option value="">Alan seçin</option>
            {(alanlar.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Secim>
          <Secim
            etiket="Ders"
            value={dersId}
            onChange={(e) => {
              setDersId(e.target.value);
              setKitapId("");
            }}
            disabled={alanId === ""}
          >
            <option value="">{alanId === "" ? "Önce alan seçin" : "Ders seçin"}</option>
            {dersler.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad}
              </option>
            ))}
          </Secim>
        </div>

        <Secim
          etiket="Havuzdan kitap"
          value={kitapId}
          onChange={(e) => {
            setKitapId(e.target.value);
            if (e.target.value) setOzelAd("");
          }}
          disabled={dersId === ""}
          yardim="Havuzda yoksa aşağıya serbest ad yazın."
        >
          <option value="">{dersId === "" ? "Önce ders seçin" : "Havuzdan seçilmedi"}</option>
          {(havuz.data ?? []).map((k) => (
            <option key={k.id} value={k.id}>
              {k.ad}
              {k.yayinevi ? ` · ${k.yayinevi}` : ""}
            </option>
          ))}
        </Secim>

        <Alan
          etiket="Serbest ad"
          value={ozelAd}
          onChange={(e) => setOzelAd(e.target.value)}
          disabled={kitapId !== ""}
          yardim={
            kitapId !== ""
              ? "Havuzdan kitap seçildi; serbest ad kullanılmıyor."
              : "Havuzda olmayan bir kitap için."
          }
        />

        {ekle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ekle.error instanceof ApiHatasi ? ekle.error.message : "Kaynak eklenemedi."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={ekle.isPending} disabled={eksik} onClick={() => ekle.mutate()}>
            Listeye ekle
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

function KaynakTopluEkle({ ogrenciId, onKapat }: { ogrenciId: string; onKapat: () => void }) {
  const qc = useQueryClient();
  const [alanId, setAlanId] = useState("");
  const [dersId, setDersId] = useState("");
  const [metin, setMetin] = useState("");
  const [sonuc, setSonuc] = useState<TopluKaynakSonuc | null>(null);

  const alanlar = useQuery({ queryKey: ["alanlar"], queryFn: alanlariGetir, retry: false });
  const agac = useQuery({
    queryKey: ["agac", alanId],
    queryFn: () => agacGetir(alanId),
    enabled: alanId !== "",
    retry: false,
  });

  const gonder = useMutation({
    mutationFn: (onIzleme: boolean) =>
      ogrenciKaynakTopluEkle(ogrenciId, { dersId, metin, onIzleme }),
    onSuccess: async (s) => {
      setSonuc(s);
      if (!s.onIzleme) {
        setMetin("");
        await qc.invalidateQueries({ queryKey: ["ogrenci-kaynak", ogrenciId] });
      }
    },
  });

  const dersler = (agac.data ?? []).filter((d) => d.tur === "ders");
  const bos = metin.trim().length === 0 || dersId === "";

  return (
    <Kart
      baslik="Listeye toplu kaynak ekle"
      aciklama="Her satıra bir kaynak. Havuzda aynı adla bir kitap varsa ona bağlanır; yoksa serbest ad olarak eklenir."
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Secim
            etiket="Alan"
            value={alanId}
            onChange={(e) => {
              setAlanId(e.target.value);
              setDersId("");
            }}
          >
            <option value="">Alan seçin</option>
            {(alanlar.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Secim>
          <Secim
            etiket="Ders"
            value={dersId}
            onChange={(e) => setDersId(e.target.value)}
            disabled={alanId === ""}
          >
            <option value="">{alanId === "" ? "Önce alan seçin" : "Ders seçin"}</option>
            {dersler.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad}
              </option>
            ))}
          </Secim>
        </div>

        <MetinAlani
          etiket="Liste"
          rows={6}
          value={metin}
          onChange={(e) => setMetin(e.target.value)}
          placeholder={"345 Soru Matematik\nTYT Fizik Soru Bankası"}
        />

        {/*
          Sonuç paneli DUYURULUYOR (`role="status"`). Toplu içe aktarma
          bittiğinde ekranda beliren tek geri bildirim bu; ekran okuyucu
          kullanıcısı onu duymazsa işin bitip bitmediğini bilemez.
          Ayrım özellikle önemli: "eklenecek — henüz kaydedilmedi" ile
          "eklendi" bambaşka iki durum ve sessiz kalırsa kullanıcı
          önizlemeyi tamamlanmış sanabilir.
        */}
        {sonuc && (
          <div role="status" className="rounded-kart bg-notr-50 flex flex-col gap-2 p-3">
            <p className="text-notr-800 text-sm font-medium">
              {sonuc.onIzleme
                ? `${sonuc.eklenecek?.length ?? 0} satır eklenecek — henüz kaydedilmedi.`
                : `${sonuc.eklenen ?? 0} kaynak eklendi.`}
            </p>
            {sonuc.elenen.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-uyari-800 text-sm font-medium">
                  {sonuc.elenen.length} satır alınmadı:
                </p>
                <ul className="flex flex-col gap-0.5">
                  {sonuc.elenen.map((e) => (
                    <li key={e.satirNo} className="text-notr-600 text-xs">
                      <span className="font-medium">{e.satirNo}. satır</span> — {e.mesaj}
                      {e.ham.trim().length > 0 && (
                        <span className="text-notr-600"> ({e.ham.trim()})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {gonder.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {gonder.error instanceof ApiHatasi ? gonder.error.message : "İşlem tamamlanamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            gorunum="ikincil"
            disabled={bos}
            yukleniyor={gonder.isPending && gonder.variables === true}
            onClick={() => gonder.mutate(true)}
          >
            Önizle
          </Buton>
          <Buton
            disabled={bos}
            yukleniyor={gonder.isPending && gonder.variables === false}
            onClick={() => gonder.mutate(false)}
          >
            Uygula
          </Buton>
          <Buton gorunum="sessiz" onClick={onKapat}>
            Kapat
          </Buton>
        </div>
      </div>
    </Kart>
  );
}
